import { useState } from "react";
import T from "../../theme";
import { useIsMobile } from "../../hooks/useIsMobile";
import { submitPedidoInfo } from "../../lib/db";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * ContactModal — reusable contact form modal.
 * Used in both LandingPage and DemoBlockedPage.
 */
export function ContactModal({ onClose }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ nome: "", email: "", telefone: "", mensagem: "" });
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const fld = (key) => (e) => { setForm(f => ({ ...f, [key]: e.target.value })); setErr(""); };

  const submit = async () => {
    if (!form.nome.trim())    { setErr("Introduza o seu nome."); return; }
    if (!form.email.trim())   { setErr("Introduza o seu email."); return; }
    if (!EMAIL_RE.test(form.email.trim())) { setErr("Introduza um email válido."); return; }
    if (!form.mensagem.trim()) { setErr("Escreva a sua mensagem."); return; }
    setErr("");
    setLoading(true);
    try {
      await submitPedidoInfo(form);
      setSubmitted(true);
    } catch (e) {
      setErr(e.message || "Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "12px 16px", fontSize: 14, borderRadius: 10,
    background: T.bg, border: `1px solid ${T.border}`, color: T.text,
    fontFamily: "inherit", outline: "none", transition: "border-color 0.2s",
    boxSizing: "border-box",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 18,
          width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto",
          padding: isMobile ? "28px 22px" : "36px 32px",
          boxShadow: T.shadowLg,
          animation: "slideUp 0.25s ease",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: T.text, letterSpacing: "-0.3px" }}>
            Pedir mais informações
          </h2>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.65, marginBottom: 24 }}>
          Tem dúvidas ou quer saber mais? Envie-nos uma mensagem e responderemos brevemente.
        </p>

        {submitted ? (
          <div style={{ textAlign: "center", animation: "slideUp 0.3s ease", padding: "20px 0" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>Mensagem enviada!</h3>
            <p style={{ fontSize: 15, color: T.textMuted, lineHeight: 1.65 }}>
              Obrigado pelo seu interesse. Entraremos em contacto brevemente.
            </p>
            <button
              onClick={onClose}
              style={{ marginTop: 20, background: T.primary, color: "#fff", border: "none", borderRadius: 10, padding: "11px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Fechar
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 16, flexDirection: isMobile ? "column" : "row" }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.textMid, display: "block", marginBottom: 6 }}>Nome *</label>
                <input type="text" value={form.nome} onChange={fld("nome")} placeholder="O seu nome" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.primary}
                  onBlur={e => e.target.style.borderColor = T.border} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.textMid, display: "block", marginBottom: 6 }}>Email *</label>
                <input type="email" value={form.email} onChange={fld("email")} placeholder="email@exemplo.pt" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.primary}
                  onBlur={e => e.target.style.borderColor = T.border} />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: T.textMid, display: "block", marginBottom: 6 }}>Telefone <span style={{ fontWeight: 400, color: T.textLight }}>(opcional)</span></label>
              <input type="tel" value={form.telefone} onChange={fld("telefone")} placeholder="+351 912 345 678" style={inputStyle}
                onFocus={e => e.target.style.borderColor = T.primary}
                onBlur={e => e.target.style.borderColor = T.border} />
            </div>

            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: T.textMid, display: "block", marginBottom: 6 }}>Mensagem *</label>
              <textarea value={form.mensagem} onChange={fld("mensagem")} placeholder="Escreva a sua mensagem ou dúvida..." rows={4}
                style={{ ...inputStyle, resize: "vertical", minHeight: 100 }}
                onFocus={e => e.target.style.borderColor = T.primary}
                onBlur={e => e.target.style.borderColor = T.border} />
            </div>

            {err && <p style={{ fontSize: 13, color: "#fca5a5" }}>{err}</p>}

            <button
              onClick={submit}
              disabled={loading}
              style={{
                background: T.primary, color: "#fff", border: "none", borderRadius: 10,
                padding: "13px 28px", fontSize: 15, fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "inherit", transition: "all 0.2s", opacity: loading ? 0.7 : 1,
                alignSelf: isMobile ? "stretch" : "flex-start",
              }}
            >
              {loading ? "A enviar..." : "Enviar mensagem"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
